import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";

const DEFAULTS = { logo_url: "", brand_name: "RENTO", brand_color: "#2C3830", support_email: "", free_unlock: false };

function lighten(hex, amt) {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.min(255, (n >> 16) + amt);
  const g = Math.min(255, ((n >> 8) & 0xff) + amt);
  const b = Math.min(255, (n & 0xff) + amt);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const BrandingContext = createContext({ ...DEFAULTS, refresh: () => {} });

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/public");
      setBrand({
        logo_url: data.logo_url || "",
        brand_name: data.brand_name || "RENTO",
        brand_color: data.brand_color || "#2C3830",
        support_email: data.support_email || "",
        free_unlock: !!data.free_unlock,
      });
    } catch {
      /* keep defaults */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Apply document title + favicon whenever branding changes
  useEffect(() => {
    if (brand.brand_name) document.title = brand.brand_name;
    if (brand.logo_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = fileUrl(brand.logo_url);
    }
    // Apply brand accent colour app-wide
    const color = brand.brand_color || "#2C3830";
    const root = document.documentElement;
    root.style.setProperty("--brand", color);
    root.style.setProperty("--brand-hover", lighten(color, 16));
  }, [brand]);

  return (
    <BrandingContext.Provider value={{ ...brand, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
